/* eslint-disable react/jsx-wrap-multilines */
/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { GateFeedFilter, ObservationSort } from '../clients/type'
import { text } from '../styles/mixins/text'
import { Spacer } from './Spacer'
import { Button } from './Button'
import { Tag } from './Tag'
import { utcToDisplayDate } from '../util/datetime'
import {
    selectVehicleTypeMetadata,
    selectLoadTypeMetadata,
    selectChassisTypeMetadata,
} from '../store/metadata'
import { isEqual } from '../util/isEqual'
import { formatPlural } from '../util/string'
import { selectSiteDirectory } from '../store/sites'
import { COLUMN_FILTER_SELECT_TYPE_IDS } from '../models/client/observation'
import { insertTestingSelector } from '../util/testing'

const SELECT_TYPE_IDS_KEYS: string[] = [...COLUMN_FILTER_SELECT_TYPE_IDS]

const ConnectedFilterTag = ({
    filter,
    disableClear,
    onClear,
}: {
    disableClear?: boolean
    filter: GateFeedFilter
    onClear?: (filter: GateFeedFilter) => void
}): JSX.Element | null => {
    const vehicleTypes = useSelector(selectVehicleTypeMetadata, isEqual)
    const loadTypes = useSelector(selectLoadTypeMetadata, isEqual)
    const chassisTypes = useSelector(selectChassisTypeMetadata, isEqual)
    switch (filter.type) {
        case 'site':
            return (
                <Tag
                    key={JSON.stringify(filter)}
                    tagKey={JSON.stringify(filter)}
                    label={
                        <LabelContainer>
                            <strong>{filter.label}:</strong>
                            &nbsp;
                            {filter.value.label}
                        </LabelContainer>
                    }
                    onClear={disableClear ? undefined : () => onClear?.(filter)}
                />
            )
        case 'gate':
            return (
                <Tag
                    key={JSON.stringify(filter)}
                    tagKey={JSON.stringify(filter)}
                    label={
                        <LabelContainer>
                            <strong>{filter.label}:</strong>
                            &nbsp;
                            {filter.value.label}
                        </LabelContainer>
                    }
                    onClear={disableClear ? undefined : () => onClear?.(filter)}
                />
            )
        case 'property': {
            let tagValue = filter.value[0].value
            let tagLabel = filter.label

            if (SELECT_TYPE_IDS_KEYS.includes(filter.value[0].name)) {
                const selectionId = filter.value[0].value
                switch (filter.value[0].name) {
                    case 'vehicle.carrierId':
                        tagLabel = 'Carrier'
                        tagValue = filter.displayValue || 'Unknown'
                        break
                    case 'chassis.typeId':
                        {
                            tagLabel = 'Chassis Type'
                            const { name = 'Unknown' } =
                                Object.values(chassisTypes || {}).find(
                                    ({ id }) => id === selectionId
                                ) || {}
                            tagValue = name
                        }
                        break
                    case 'vehicle.typeId':
                        {
                            tagLabel = 'Vehicle Type'
                            const { name = 'Unknown' } =
                                Object.values(vehicleTypes || {}).find(
                                    ({ id }) => id === selectionId
                                ) || {}
                            tagValue = name
                        }
                        break
                    case 'load.typeId':
                        {
                            tagLabel = 'Load Type'
                            const { name = 'Unknown' } =
                                Object.values(loadTypes || {}).find(
                                    ({ id }) => id === selectionId
                                ) || {}
                            tagValue = name
                        }
                        break
                    default:
                }
            }
            // FIXME: change this hack for the tag label when there's time
            return (
                <Tag
                    key={JSON.stringify(filter)}
                    tagKey={JSON.stringify(filter)}
                    label={
                        <LabelContainer>
                            <strong>{tagLabel}:</strong>
                            &nbsp;
                            {tagValue}
                        </LabelContainer>
                    }
                    onClear={() => onClear?.(filter)}
                />
            )
        }
        case 'isBare':
            return (
                <Tag
                    key={`IsBare_${JSON.stringify(filter.value)}`}
                    tagKey={`IsBare_${JSON.stringify(filter.value)}`}
                    label={
                        <LabelContainer>
                            <strong>{filter.label}:</strong>
                            &nbsp;
                            {filter.value === true ? 'Yes' : 'No'}
                        </LabelContainer>
                    }
                    onClear={() => onClear?.(filter)}
                />
            )
        case 'direction':
            return (
                <Tag
                    key={`direction_${JSON.stringify(filter.value)}`}
                    tagKey={`direction_${JSON.stringify(filter.value)}`}
                    label={
                        <LabelContainer>
                            <strong>{filter.label}:</strong>
                            &nbsp;
                            {filter.value === 'true' ? 'inbound' : 'outbound'}
                        </LabelContainer>
                    }
                    onClear={() => onClear?.(filter)}
                />
            )
        case 'date': {
            const label = []
            if (filter.value.start) label.push(utcToDisplayDate(filter.value.start))
            if (filter.value.end) label.push(utcToDisplayDate(filter.value.end))
            return (
                <Tag
                    key={JSON.stringify(filter)}
                    tagKey={JSON.stringify(filter)}
                    label={
                        <LabelContainer>
                            <strong>{filter.label}:</strong>
                            &nbsp;
                            {label.join('-')}
                        </LabelContainer>
                    }
                    onClear={() => onClear?.(filter)}
                />
            )
        }
        default:
            return null
    }
}

type Props = {
    filters: GateFeedFilter[]
    sort: ObservationSort | null
    onRemoveFilter: (filter: GateFeedFilter) => void
    onClearAllFilters: () => void
    onRemoveSort: () => void
}

export const ConnectedObservationTags = (props: Props): JSX.Element => {
    const { filters, sort, onRemoveFilter, onClearAllFilters, onRemoveSort } = props
    const sites = useSelector(selectSiteDirectory, isEqual)

    const showFilters = Boolean(sites) && (filters.length > 0 || !!sort)
    let sortField = ''
    if (sort) {
        switch (sort.column) {
            case 'loadDwellTimeHr':
                sortField = 'Load Dwell'
                break
            case 'vehicleDwellTimeHr':
                sortField = 'Vehicle Dwell'
                break
            case 'chassisDwellTimeHr':
                sortField = 'Chassis Dwell'
                break
            default:
        }
    }
    return (
        <Container>
            {showFilters ? (
                <>
                    <FilterSectionHeader>
                        Active Filters
                        <Spacer size={2} />
                        <Button
                            label="Clear All"
                            iconKey="Clear"
                            size="small"
                            type="text"
                            testingKeys={['GateFeedFilterTagsClearAllButton']}
                            onClick={() => {
                                onClearAllFilters()
                                onRemoveSort()
                            }}
                        />
                    </FilterSectionHeader>
                    <Spacer vertical size={4} />
                </>
            ) : (
                <div />
            )}
            <SelectedFilters>
                {showFilters ? (
                    filters.map((filter, index) => (
                        <>
                            {index !== 0 && <Spacer size={2} />}
                            <div {...insertTestingSelector('GateFeedFilterTag')}>
                                <ConnectedFilterTag
                                    filter={filter}
                                    onClear={() => {
                                        onRemoveFilter(filter)
                                    }}
                                />
                            </div>
                        </>
                    ))
                ) : (
                    <div />
                )}
                {sort ? (
                    <>
                        {filters.length ? <Spacer size={4} /> : <div />}
                        <Tag
                            key="sort"
                            tagKey="sort"
                            label={
                                <LabelContainer>
                                    <strong>{sortField}:</strong>
                                    &nbsp;
                                    {`sort ${
                                        sort.direction === 'ASC' ? 'ascending' : 'descending'
                                    }`}
                                </LabelContainer>
                            }
                            onClear={() => onRemoveSort()}
                        />
                    </>
                ) : (
                    <div />
                )}
                {!showFilters ? (
                    <ConnectedFilterTag
                        disableClear
                        filter={{
                            label: 'Sites',
                            type: 'site',
                            id: 'ALL_SITES',
                            value: {
                                label: `all ${formatPlural({
                                    count: Object.keys(sites || {}).length,
                                    value: 'site',
                                })} selected`,
                                value: 'ALL',
                            },
                        }}
                    />
                ) : (
                    <div />
                )}
            </SelectedFilters>
        </Container>
    )
}

const Container = styled.div`
    display: flex;
    flex-flow: column nowrap;
    flex: 0 1 auto;
`

const SelectedFilters = styled.div`
    display: flex;
    flex-flow: row wrap;
    flex: 1 1 auto;
    min-width: 0;
`

const FilterSectionHeader = styled.div`
    ${text('title', 'primary', 'text', 'bold')}
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
`

const LabelContainer = styled.span`
    white-space: nowrap;
`
